import { useState, useEffect, useCallback } from 'react';
import { useUsers } from '../../../hooks/admin/userManagement/useUsers';
import { useUser } from '../../../context/UserContext';

/**
 * Hook for user selection with debounced search.
 * Defaults to currently logged-in user.
 *
 * @param {number} debounceMs - Debounce delay in milliseconds (default: 2000)
 * @returns {Object} users list, loading state, error, currentUser
 */
export function useUserSelector(debounceMs = 500) {
  const { user: currentUser } = useUser();
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, debounceMs);

    return () => clearTimeout(timer);
  }, [searchTerm, debounceMs]);

  // Fetch users when debounced search term changes
  const shouldSearch = debouncedSearchTerm.trim().length >= 2;
  const {
    users = [],
    isLoading: loading,
    error,
  } = useUsers(
    {
      search: debouncedSearchTerm.trim(),
      limit: 20,
      isActive: true,
    },
    { enabled: shouldSearch }
  );

  return {
    users,
    loading,
    error: error?.message ?? error ?? null,
    searchTerm,
    setSearchTerm,
    debouncedSearchTerm,
    currentUser,
  };
}
